<template>
  <div class="d-flex align-items-center">
    <b-dropdown
      variant="flat-secondary"
      right
      no-caret
      toggle-class="p-50"
      menu-class="customer-chat-participants-menu"
      :title="`Participantes (${participants.length})`"
    >
      <template #button-content>
        <feather-icon
          icon="UsersIcon"
          size="18"
          :badge="invited.length || null"
          badge-classes="badge-primary"
        />
      </template>

      <b-dropdown-header>Participantes</b-dropdown-header>
      <b-dropdown-text
        v-for="participant in participants"
        :key="participant.participantId || participant.principal"
        text-class="customer-chat-participant"
      >
        <div class="d-flex align-items-start justify-content-between">
          <div class="overflow-hidden mr-1">
            <span class="d-block text-truncate font-weight-bold">{{ participant.name }}</span>
            <small class="d-block text-muted text-truncate">
              {{ describe(participant) }}
            </small>
            <small
              v-if="participant.inviteNote"
              class="d-block text-muted text-truncate"
              :title="participant.inviteNote"
            >
              Nota: {{ participant.inviteNote }}
            </small>
          </div>
          <b-button
            v-if="canRemove(participant)"
            v-b-tooltip.hover.left
            variant="flat-danger"
            size="sm"
            class="btn-icon flex-shrink-0"
            title="Remover da conversa"
            :disabled="busy"
            @click.stop="confirmRemove(participant)"
          >
            <feather-icon icon="UserMinusIcon" />
          </b-button>
        </div>
      </b-dropdown-text>

      <template v-if="canManage || leaveable">
        <b-dropdown-divider />
        <b-dropdown-item
          v-if="canManage"
          :disabled="busy"
          @click="showDirect = true"
        >
          <feather-icon
            icon="UserPlusIcon"
            class="mr-50"
          />
          Direcionar conversa
        </b-dropdown-item>
        <b-dropdown-item
          v-if="leaveable"
          variant="danger"
          :disabled="busy"
          @click="confirmLeave"
        >
          <feather-icon
            icon="LogOutIcon"
            class="mr-50"
          />
          Sair da conversa
        </b-dropdown-item>
      </template>
    </b-dropdown>

    <direct-conversation-sidebar
      v-if="canManage"
      :visible.sync="showDirect"
      :conversation="conversation"
      @invited="onInvited"
    />
  </div>
</template>

<script>
import {
  BDropdown, BDropdownHeader, BDropdownText, BDropdownDivider, BDropdownItem, BButton, VBTooltip,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { CUSTOMER_CHAT_RESOURCE, CUSTOMER_CHAT_WRITE_ACTION } from '@/constants/resources'
import * as types from '../store/types'
import { participantLabel } from '../utils/messages'
import { formatDateTime } from '../utils/format'
import {
  ROLE_OWNER,
  ROLE_INVITED,
  canManageParticipants,
  canLeave,
  historyScopeLabel,
} from '../utils/participants'
import DirectConversationSidebar from './DirectConversationSidebar.vue'

export default {
  components: {
    BDropdown,
    BDropdownHeader,
    BDropdownText,
    BDropdownDivider,
    BDropdownItem,
    BButton,
    DirectConversationSidebar,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    conversation: {
      type: Object,
      required: true,
    },
  },
  setup() {
    return { toast: useToast() }
  },
  data() {
    return {
      showDirect: false,
      busy: false,
    }
  },
  computed: {
    ...mapGetters({
      myPrincipal: types.MY_PRINCIPAL,
    }),
    participants() {
      return this.conversation.participants || []
    },
    invited() {
      return this.participants.filter(participant => participant.role === ROLE_INVITED)
    },
    canManage() {
      return canManageParticipants(this.conversation, this.$can(CUSTOMER_CHAT_WRITE_ACTION, CUSTOMER_CHAT_RESOURCE))
    },
    leaveable() {
      return canLeave(this.conversation, this.myPrincipal)
    },
  },
  methods: {
    describe(participant) {
      const parts = []
      if (participant.unitLabel) parts.push(participant.unitLabel)
      parts.push(participant.role === ROLE_OWNER ? 'Responsável' : historyScopeLabel(participant))
      if (participant.expiresAt) parts.push(`até ${formatDateTime(participant.expiresAt)}`)
      return parts.join(' · ')
    },
    canRemove(participant) {
      return this.canManage && participant.role === ROLE_INVITED && !!participant.participantId
    },
    confirmRemove(participant) {
      this.$bvModal.msgBoxConfirm(
        `${participantLabel(participant)} deixará de ver e responder esta conversa. O cliente verá que a pessoa saiu.`,
        {
          title: 'Remover da conversa?',
          okTitle: 'Remover',
          okVariant: 'danger',
          cancelTitle: 'Cancelar',
          cancelVariant: 'outline-secondary',
          centered: true,
        },
      ).then(ok => {
        if (ok) this.remove(participant, 'Participante removido.')
      })
    },
    confirmLeave() {
      const me = this.participants.find(participant => participant.principal === this.myPrincipal)
      if (!me) return
      this.$bvModal.msgBoxConfirm(
        'Você deixará de ver esta conversa. Para voltar, o responsável precisa convidar você de novo.',
        {
          title: 'Sair da conversa?',
          okTitle: 'Sair',
          okVariant: 'danger',
          cancelTitle: 'Cancelar',
          cancelVariant: 'outline-secondary',
          centered: true,
        },
      ).then(ok => {
        if (ok) this.remove(me, 'Você saiu da conversa.')
      })
    },
    remove(participant, successText) {
      this.busy = true
      this.$store.dispatch(types.REMOVE_PARTICIPANT, {
        conversationId: this.conversation.conversationId,
        participant,
      })
        .then(() => this.notify(successText, 'CheckIcon', 'success'))
        .catch(() => this.notify('Não foi possível concluir. Tente novamente.', 'AlertTriangleIcon', 'danger'))
        .finally(() => {
          this.busy = false
        })
    },
    onInvited(participant) {
      this.notify(`${participantLabel(participant)} agora participa da conversa.`, 'CheckIcon', 'success')
    },
    notify(text, icon, variant) {
      this.toast({
        component: ToastificationContent,
        props: {
          title: 'Chat com clientes',
          text,
          icon,
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss">
.customer-chat-participants-menu {
  min-width: 18rem;
  max-width: 22rem;
  .customer-chat-participant {
    padding-top: 0.35rem;
    padding-bottom: 0.35rem;
  }
}
</style>
